import PropTypes from 'prop-types';
import { Button, ButtonGroup } from 'react-bootstrap';
import { FaPrint } from 'react-icons/fa';
import { useState } from 'react';
import { getActiveAccountId } from '../utils/accountSessions';

const CompactReferralView = ({ referral, className = '', style = {} }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintingFull, setIsPrintingFull] = useState(false);

  const handlePrint = async () => {
    if (!referral || !referral.id) {
      console.error('Referral ID is required for PDF print');
      return;
    }

    setIsPrinting(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const activeAccountId = getActiveAccountId();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        headers['X-Account-Id'] = String(activeAccountId);
      }
      
      // Step 1: Generate and store the PDF
      const generateResponse = await fetch(`${API_URL}referrals/${referral.id}/pdf`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!generateResponse.ok) {
        let serverMessage = '';
        try {
          const contentType = generateResponse.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await generateResponse.json();
            serverMessage = data?.error || data?.message || JSON.stringify(data);
          } else {
            serverMessage = await generateResponse.text();
          }
        } catch (_) {
          // ignore parse errors
        }
        const msg = `Failed to generate PDF: ${generateResponse.status}${serverMessage ? ` - ${serverMessage}` : ''}`;
        throw new Error(msg);
      }

      const generateData = await generateResponse.json();
      const pdfId = generateData.pdf_id;

      if (!pdfId) {
        throw new Error('PDF ID not received from server');
      }

      // Step 2: Get signed URL for the PDF
      const signedUrlHeaders = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        signedUrlHeaders['X-Account-Id'] = String(activeAccountId);
      }

      const signedUrlResponse = await fetch(`${API_URL}referral-pdfs/${pdfId}/signed-url`, {
        method: 'GET',
        headers: signedUrlHeaders,
        credentials: 'include',
      });

      if (!signedUrlResponse.ok) {
        throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
      }

      const signedUrlData = await signedUrlResponse.json();
      const pdfUrl = signedUrlData.url;

      // Open PDF directly using signed URL (works in iOS WebView)
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (!printWindow) {
        alert('Please allow popups for this site to print the referral.');
      }
    } catch (error) {
      console.error('Error printing referral PDF:', error);
      alert(`Failed to print referral PDF. Error: ${error?.message || error}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintFull = async () => {
    if (!referral || !referral.id) {
      console.error('Referral ID is required for PDF print');
      return;
    }

    setIsPrintingFull(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const activeAccountId = getActiveAccountId();
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        headers['X-Account-Id'] = String(activeAccountId);
      }
      
      // Step 1: Generate and store the PDF
      const generateResponse = await fetch(`${API_URL}referrals/${referral.id}/pdf-a4`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });

      if (!generateResponse.ok) {
        let serverMessage = '';
        try {
          const contentType = generateResponse.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await generateResponse.json();
            serverMessage = data?.error || data?.message || JSON.stringify(data);
          } else {
            serverMessage = await generateResponse.text();
          }
        } catch (_) {
          // ignore parse errors
        }
        const msg = `Failed to generate A4 PDF: ${generateResponse.status}${serverMessage ? ` - ${serverMessage}` : ''}`;
        throw new Error(msg);
      }

      const generateData = await generateResponse.json();
      const pdfId = generateData.pdf_id;

      if (!pdfId) {
        throw new Error('PDF ID not received from server');
      }

      // Step 2: Get signed URL for the PDF
      const signedUrlHeaders = {
        'Content-Type': 'application/json',
      };
      
      if (activeAccountId) {
        signedUrlHeaders['X-Account-Id'] = String(activeAccountId);
      }

      const signedUrlResponse = await fetch(`${API_URL}referral-pdfs/${pdfId}/signed-url`, {
        method: 'GET',
        headers: signedUrlHeaders,
        credentials: 'include',
      });

      if (!signedUrlResponse.ok) {
        throw new Error(`Failed to get signed URL: ${signedUrlResponse.status}`);
      }

      const signedUrlData = await signedUrlResponse.json();
      const pdfUrl = signedUrlData.url;

      // Open PDF directly using signed URL (works in iOS WebView)
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (!printWindow) {
        alert('Please allow popups for this site to print the referral.');
      }
    } catch (error) {
      console.error('Error printing referral PDF (A4):', error);
      alert(`Failed to print referral (A4). Error: ${error?.message || error}`);
    } finally {
      setIsPrintingFull(false);
    }
  };

  if (!referral) return null;

  return (
    <ButtonGroup size="sm" className={`ios-app ref-actions ${className}`.trim()}>
      <Button
        variant="outline-primary"
        onClick={handlePrint}
        disabled={isPrinting}
        title="Print referral (A5)"
        aria-label="Print referral (A5)"
        style={{ ...style }}
      >
        <FaPrint className="me-1" /> {isPrinting ? 'Loading...' : 'Compact'}
      </Button>
      <Button
        variant="outline-primary"
        onClick={handlePrintFull}
        disabled={isPrintingFull}
        title="Print referral (Full A4)"
        aria-label="Print referral (Full A4)"
        style={{ ...style }}
      >
        <FaPrint className="me-1" /> {isPrintingFull ? 'Loading...' : 'Full'}
      </Button>
    </ButtonGroup>
  );
};

CompactReferralView.propTypes = {
  referral: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    to: PropTypes.string,
  }),
  className: PropTypes.string,
  style: PropTypes.object,
};

export default CompactReferralView;
